import { Modal, StatusBar } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import styles from './ProductPublishModal.module.scss';
import { useNavigate } from 'react-router-dom';
import { usePrefetchSchedules } from 'src/hooks/useSchedules/useSchedules';
import { ScheduleStatus } from 'src/api/getSchedules/types';
import chefPlating from 'src/assets/images/chef-plating.png';
import { SelectPublishType } from './steps/SelectPublishType/SelectPublishType';
import { ExcludeProducts } from './steps/ExcludeProducts';
import { SummaryView } from './steps/SummaryView';
import { IPublishFormValues, PublishType, Steps } from './types';
import { useState } from 'react';
import { SelectVenues } from './steps/SelectVenues/SelectVenues';
import { SelectInstance } from './steps/SelectInstance';
import { usePublishProducts } from 'src/hooks/usePublishProducts/usePublishProducts';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { PublishProductsType } from 'src/api/publishProducts/types';
import { errorNotification, successNotification } from './Notifications';
import { PublishDetailsView } from '../PublishDetailsView/PublishDetailsView';
import { Routes } from 'src/routes';
import { useValidate } from 'src/hooks/useValidate/useValidate';
import { getValidationRules } from 'src/utils/schedules/getValidationRules';
import { EVENT_TYPES } from 'src/api/getEvents/types';
import { FEATURE_FLAGS } from 'src/constants';
import { useAuthWrapper } from '@weareopenr/authkit';

interface IProductPublishModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

const Content = ({ closeModal }: Omit<IProductPublishModalProps, 'isOpen'>) => {
  const [step, setStep] = useState<Steps>(Steps.type);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishRequest, setPublishRequest] = useState<IPublishFormValues>({
    type: PublishType.PRODUCT,
    priceSchemeId: null,
    instanceId: null,
    excludedItems: [],
    venues: [],
    scheduleName: undefined,
    publicationDate: null,
  });
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { trackEvent, captureException } = useUserTracking();
  const navigate = useNavigate();
  const prefetchSchedules = usePrefetchSchedules(ScheduleStatus.SCHEDULED);
  const publishProducts = usePublishProducts();
  const validate = useValidate();
  const { featureFlags } = useAuthWrapper();
  const enhancedScheduling = featureFlags.includes(FEATURE_FLAGS.ENHANCED_SCHEDULING);

  const saveData = (data: Partial<IPublishFormValues>, nextStep?: Steps) => {
    setPublishRequest((prev) => ({ ...prev, ...data }));
    if (nextStep === undefined) {
      setStep((prev) => prev + 1);
    } else {
      setStep(nextStep);
    }
  };

  const getPublishTypes = (type: PublishType): PublishProductsType[] => {
    const typeMap: Record<PublishType, PublishProductsType[]> = {
      [PublishType.PRODUCT]: [PublishProductsType.PRODUCT],
      [PublishType.PRICING]: [PublishProductsType.PRICING],
    };
    return typeMap[type];
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    clearError();
    try {
      const enhancedSchedule = publishRequest.publicationDate && enhancedScheduling;

      const includesPricing = publishRequest.type === PublishType.PRICING;

      const { event_id, schedule_ids, schedule_name } = await publishProducts.mutateAsync({
        publish_type: getPublishTypes(publishRequest.type),
        ...(publishRequest.instanceId && { instance_id: publishRequest.instanceId }),
        ...(includesPricing && {
          price_scheme_id: publishRequest.priceSchemeId || undefined,
          venues: publishRequest.venues,
        }),
        schedule_name: publishRequest.scheduleName ?? undefined,
        excluded_item_ids: publishRequest.excludedItems.map(({ item_id }) => item_id).filter(Boolean),
        publication_date_time: publishRequest.publicationDate
          ? publishRequest.publicationDate.toISOString()
          : undefined,
      });

      trackEvent('products_published', {
        publishType: publishRequest.type,
        instanceId: publishRequest.instanceId,
        venueCount: publishRequest.venues.length,
        excludedCount: publishRequest.excludedItems.length,
        isScheduled: !!publishRequest.publicationDate,
      });

      if (enhancedSchedule) {
        await validate.mutateAsync({
          criteria: {
            schedule_id: schedule_ids[0],
            ...(publishRequest.priceSchemeId ? { price_scheme_id: publishRequest.priceSchemeId } : {}),
            rules: getValidationRules(includesPricing ? EVENT_TYPES.PUBLISH_PRICING : EVENT_TYPES.PUBLISH_PRODUCT),
          },
        });
        await prefetchSchedules();
      }
      showNotification(
        successNotification(publishRequest.type, publishRequest.publicationDate?.toISOString(), schedule_name),
      );
      if (enhancedSchedule) {
        navigate(Routes.PUBLISH_SCHEDULE, { state: { newScheduleId: schedule_ids[0] } });
      } else {
        navigate(`${Routes.PRODUCT_LIBRARY}/status/${event_id}`);
      }
    } catch (error) {
      setIsPublishing(false);
      captureException(error, { tags: { action: 'publish_products', publishType: publishRequest.type } });
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      return showNotification(
        errorNotification(publishRequest.type, publishRequest.publicationDate?.toISOString(), errorMsg),
      );
    }
  };

  return (
    <>
      {step === Steps.type && (
        <SelectPublishType closeModal={closeModal} submitDataToParent={saveData} defaultValues={publishRequest} />
      )}
      {step === Steps.publishDetails && (
        <PublishDetailsView
          defaultPublicationDate={publishRequest.publicationDate}
          publishType={publishRequest.type}
          defaultPriceSchemeId={publishRequest.priceSchemeId}
          defaultScheduleName={publishRequest.scheduleName}
          submitDataToParent={saveData}
        />
      )}
      {step === Steps.instance && <SelectInstance submitDataToParent={saveData} defaultValues={publishRequest} />}
      {step === Steps.venues && <SelectVenues submitDataToParent={saveData} defaultValues={publishRequest} />}
      {step === Steps.excludeProducts && (
        <ExcludeProducts submitDataToParent={saveData} defaultValues={publishRequest} />
      )}

      {step === Steps.summary && (
        <SummaryView
          submitDataToParent={saveData}
          defaultValues={publishRequest}
          onPublish={handlePublish}
          isLoading={isPublishing}
        />
      )}
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </>
  );
};

export const ProductPublishModal = ({ isOpen, closeModal }: IProductPublishModalProps) => {
  return (
    <Modal open={isOpen} image={chefPlating} imageAlt="Picture of chef at the plating station" closeModal={closeModal}>
      <Content closeModal={closeModal} />
    </Modal>
  );
};
